import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { scrollableTableWrapStyles, scrollableTableSizing } from '../styles/scrollableTable';
import { FiDollarSign, FiRefreshCw, FiSearch } from 'react-icons/fi';
import { supabase } from '../../config/supabase';
import { useAuthContext } from '../../hooks/useAuthContext';

const Container = styled.div`
  padding: 32px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin-top: 8px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 16px;
  gap: 8px;
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  font-size: 14px;
  width: 300px;
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: white;
  color: #1f2937;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
`;

const TableContainer = styled.div`
  ${scrollableTableWrapStyles}
`;

const Table = styled.table`
  ${scrollableTableSizing}
  border-collapse: collapse;
`;

const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const TableCell = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #1f2937;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: top;
`;

const TableRow = styled.tr`
  transition: background 0.2s;

  &:hover {
    background: #f9fafb;
  }

  &:last-child ${TableCell} {
    border-bottom: none;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 64px 32px;
  color: #6b7280;
`;

const PrimaryBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: #d1fae5;
  color: #065f46;
`;

const BrandBadge = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  background: #e0f2fe;
  color: #0369a1;
`;

const InfoText = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-top: 4px;
  line-height: 1.5;
`;

function SuperAdminBankDetails() {
  const { session } = useAuthContext();
  const [bankDetails, setBankDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchBankDetails();
  }, []);

  const fetchBankDetails = async () => {
    try {
      setLoading(true);

      console.log('=== SuperAdmin Bank Details - Simple Approach ===');
      console.log('User ID:', session?.user?.id);

      // Simple approach: First get all bank details, then manually fetch brand info
      const { data: bankData, error: bankError } = await supabase
        .from('bank_details')
        .select('*')
        .eq('is_primary', true)
        .order('created_at', { ascending: false });

      console.log('Bank details query:', {
        count: bankData?.length || 0,
        error: bankError
      });

      if (bankError) {
        console.error('Error fetching bank details:', bankError);
        throw bankError;
      }

      if (!bankData || bankData.length === 0) {
        console.log('ℹ️ No bank details found in database');
        setBankDetails([]);
        return;
      }

      // Get all unique brand IDs
      const brandIds = [...new Set(bankData.map(bd => bd.brand_id).filter(Boolean))];
      console.log('Found brand IDs:', brandIds);

      // Fetch brand information separately
      const { data: brandsData, error: brandsError } = await supabase
        .from('brands')
        .select('id, name, slug, active')
        .in('id', brandIds);

      console.log('Brands query:', {
        count: brandsData?.length || 0,
        error: brandsError
      });

      if (brandsError) {
        console.warn('Error fetching brands:', brandsError);
        // Continue anyway, just without brand info
      }

      // Create a map of brand info for quick lookup
      const brandsMap = {};
      if (brandsData) {
        brandsData.forEach(brand => {
          brandsMap[brand.id] = brand;
        });
      }

      // Combine bank details with brand info
      const combinedData = bankData.map(bankDetail => ({
        ...bankDetail,
        brand: brandsMap[bankDetail.brand_id] || null
      }));

      console.log('✅ Successfully combined data:', combinedData.length, 'records');
      setBankDetails(combinedData);

    } catch (error) {
      console.error('❌ Error fetching bank details:', error);
      alert(`Error fetching bank details: ${error.message}`);
      setBankDetails([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter bank details by search term
  const filteredBankDetails = bankDetails.filter(detail => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      detail.brand?.name?.toLowerCase().includes(searchLower) ||
      detail.account_holder_name?.toLowerCase().includes(searchLower) ||
      detail.bank_name?.toLowerCase().includes(searchLower) ||
      detail.account_number_iban?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <Container>
      <Header>
        <div>
          <Title>
            <FiDollarSign /> All Brands Bank Details
          </Title>
          <Subtitle>View bank account information for all brands</Subtitle>
        </div>
        <HeaderActions>
          <SearchBar>
            <FiSearch />
            <SearchInput
              type="text"
              placeholder="Search by brand, bank, account holder..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBar>
          <RefreshButton onClick={fetchBankDetails}>
            <FiRefreshCw /> Refresh
          </RefreshButton>
        </HeaderActions>
      </Header>

      <Card>
        <TableContainer>
          <Table>
            <thead>
              <TableRow>
                <TableHeaderCell>Brand</TableHeaderCell>
                <TableHeaderCell>Account Holder</TableHeaderCell>
                <TableHeaderCell>Bank Name</TableHeaderCell>
                <TableHeaderCell>Bank Address</TableHeaderCell>
                <TableHeaderCell>Account Number / IBAN</TableHeaderCell>
                <TableHeaderCell>SWIFT/BIC</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Created</TableHeaderCell>
              </TableRow>
            </thead>
            <tbody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} style={{ textAlign: 'center', padding: '40px' }}>
                    Loading bank details...
                  </TableCell>
                </TableRow>
              ) : filteredBankDetails.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <EmptyState>
                      {searchTerm ? (
                        <>
                          <p style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>
                            No bank details found
                          </p>
                          <p>No bank details match your search criteria</p>
                        </>
                      ) : (
                        <>
                          <p style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>
                            No bank details available
                          </p>
                          <p>Brands haven't added their bank details yet</p>
                        </>
                      )}
                    </EmptyState>
                  </TableCell>
                </TableRow>
              ) : (
                filteredBankDetails.map(bank => (
                  <TableRow key={bank.id}>
                    <TableCell>
                      {bank.brand ? (
                        <>
                          <BrandBadge>{bank.brand.name}</BrandBadge>
                          <InfoText>
                            {bank.brand.active ? '✓ Active' : '✗ Inactive'}
                          </InfoText>
                        </>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>No Brand</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <strong>{bank.account_holder_name}</strong>
                    </TableCell>
                    <TableCell>{bank.bank_name}</TableCell>
                    <TableCell>
                      <div style={{ maxWidth: '200px', fontSize: '13px', lineHeight: '1.5' }}>
                        {bank.bank_address}
                      </div>
                    </TableCell>
                    <TableCell>
                      <code style={{
                        background: '#f3f4f6',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '13px',
                        fontFamily: 'monospace'
                      }}>
                        {bank.account_number_iban}
                      </code>
                    </TableCell>
                    <TableCell>
                      {bank.swift_bic_code ? (
                        <code style={{
                          background: '#f3f4f6',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '13px',
                          fontFamily: 'monospace'
                        }}>
                          {bank.swift_bic_code}
                        </code>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>N/A</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {bank.is_primary && <PrimaryBadge>Primary</PrimaryBadge>}
                    </TableCell>
                    <TableCell>
                      {bank.created_at
                        ? new Date(bank.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })
                        : '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </TableContainer>
      </Card>
    </Container>
  );
}

export default SuperAdminBankDetails;
